import { useRef, useEffect } from "react";
import * as THREE from "three";

/* Reusable WebGL section background.
   variant: "embers" | "seigaiha" | "asanoha"
   - Looping, responsive (ResizeObserver), cursor-interactive (uMouse)
   - Paused when off-screen (IntersectionObserver) or tab hidden
   Kept dark/low-contrast so section text stays readable. */

const PATTERN_VERT = `
  varying vec2 vUv;
  void main(){
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const NOISE_GLSL = `
  float hash1(vec2 p){ p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
  vec2 hash2(vec2 p){ p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3))); return fract(sin(p) * 43758.5453); }
  float vnoise(vec2 p){
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = hash1(i), b = hash1(i + vec2(1.0, 0.0));
    float c = hash1(i + vec2(0.0, 1.0)), d = hash1(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }
  float fbm(vec2 p){
    float v = 0.0, a = 0.5;
    for(int i = 0; i < 6; i++){ v += a * vnoise(p); p = p * 2.02 + 7.1; a *= 0.5; }
    return v;
  }
`;

const INK_FRAG = `
  precision highp float;
  varying vec2 vUv;
  uniform vec2 uRes;
  uniform float uTime;
  uniform vec2 uMouse;
  uniform float uIntensity;
  uniform vec3 uColor;
  uniform vec3 uColorHot;
  ${NOISE_GLSL}
  void main(){
    float aspect = uRes.x / uRes.y;
    vec2 p = vec2(vUv.x * aspect, vUv.y) * 3.0;
    float t = uTime * 0.06;

    // cursor swirl
    vec2 m = vec2((uMouse.x * 0.5 + 0.5) * aspect, uMouse.y * 0.5 + 0.5) * 3.0;
    vec2 toM = p - m;
    float md = length(toM);
    float sw = exp(-md * 1.3) * 1.6;
    float ca = cos(sw), sa = sin(sw);
    p = m + mat2(ca, -sa, sa, ca) * (p - m);

    // domain-warped fbm (flowing ink)
    vec2 q = vec2(fbm(p + t), fbm(p + vec2(5.2, 1.3) - t));
    vec2 r = vec2(fbm(p + 1.6 * q + vec2(1.7, 9.2) + t * 0.6),
                  fbm(p + 1.6 * q + vec2(8.3, 2.8) - t * 0.6));
    float f = fbm(p + 2.0 * r);
    f = smoothstep(0.25, 1.05, f);

    float vein = pow(f, 2.2);
    vec3 col = uColor * f * 0.85 + uColorHot * vein * 0.32;
    col += uColor * exp(-md * 2.2) * 0.5;          // cursor glow
    float a = f * uIntensity;
    gl_FragColor = vec4(col * a, a);
  }
`;

const KINTSUGI_FRAG = `
  precision highp float;
  varying vec2 vUv;
  uniform vec2 uRes;
  uniform float uTime;
  uniform vec2 uMouse;
  uniform float uIntensity;
  uniform vec3 uColor;
  uniform vec3 uColorHot;
  ${NOISE_GLSL}

  float voronoiEdge(vec2 x, float t){
    vec2 n = floor(x), f = fract(x);
    vec2 mg, mr; float md = 8.0;
    for(int j=-1;j<=1;j++) for(int i=-1;i<=1;i++){
      vec2 g = vec2(float(i), float(j));
      vec2 o = hash2(n + g);
      o = 0.5 + 0.5 * sin(t + 6.2831 * o);
      vec2 r = g + o - f;
      float d = dot(r, r);
      if(d < md){ md = d; mr = r; mg = g; }
    }
    md = 8.0;
    for(int j=-2;j<=2;j++) for(int i=-2;i<=2;i++){
      vec2 g = mg + vec2(float(i), float(j));
      vec2 o = hash2(n + g);
      o = 0.5 + 0.5 * sin(t + 6.2831 * o);
      vec2 r = g + o - f;
      if(dot(mr - r, mr - r) > 0.0001)
        md = min(md, dot(0.5 * (mr + r), normalize(r - mr)));
    }
    return md;
  }

  void main(){
    float aspect = uRes.x / uRes.y;
    vec2 p = vec2(vUv.x * aspect, vUv.y) * 5.0;
    float t = uTime * 0.12;

    float d = voronoiEdge(p, t);
    float seam = smoothstep(0.055, 0.0, d);      // sharp gold seam
    float halo = exp(-d * 8.0) * 0.6;            // soft bleed

    // gold seams with a crimson undertone (kintsugi)
    vec3 gold = uColorHot;
    vec3 col = gold * seam + uColor * halo * 0.5;

    // cursor heats the nearby seams
    vec2 m = vec2((uMouse.x * 0.5 + 0.5) * aspect, uMouse.y * 0.5 + 0.5) * 5.0;
    float mdist = distance(p, m);
    float heat = exp(-mdist * 0.7);
    col += gold * seam * heat * 1.1;

    float a = (seam + halo * 0.5) * (0.6 + heat * 0.6) * uIntensity;
    gl_FragColor = vec4(col * a, a);
  }
`;

const EMBER_VERT = `
  attribute vec4 aData; // x:unused y:speed z:sizeSeed w:phase
  uniform float uTime;
  uniform vec2 uMouse;
  uniform float uAspect;
  uniform float uPixelRatio;
  varying float vAlpha;
  void main(){
    float speed = aData.y;
    float sizeSeed = aData.z;
    float phase = aData.w;

    float y = fract((position.y * 0.5 + 0.5) + uTime * speed * 0.025);
    float ny = y * 2.0 - 1.0;
    float sway = sin(uTime * (0.4 + speed * 0.5) + phase * 6.28) * 0.05;
    vec2 pos = vec2(position.x + sway, ny);

    // cursor repel / swirl
    vec2 d = pos - uMouse;
    d.x *= uAspect;
    float dist = length(d);
    float infl = smoothstep(0.4, 0.0, dist);
    vec2 dir = normalize(d + 0.0001);
    pos += vec2(dir.x, dir.y) * infl * 0.14;
    pos.x += -dir.y * infl * 0.06; // slight swirl

    float flick = 0.55 + 0.45 * sin(uTime * 3.0 + phase * 6.28);
    // fade in from bottom, out toward top
    float edge = smoothstep(-1.05, -0.6, ny) * (1.0 - smoothstep(0.45, 1.0, ny));
    vAlpha = edge * flick;

    gl_Position = vec4(pos, 0.0, 1.0);
    gl_PointSize = sizeSeed * (7.0 + 5.0 * flick) * uPixelRatio;
  }
`;

const EMBER_FRAG = `
  precision highp float;
  varying float vAlpha;
  uniform vec3 uColorHot;
  uniform vec3 uColorEdge;
  void main(){
    vec2 c = gl_PointCoord - 0.5;
    float r = length(c) * 2.0;
    if(r > 1.0) discard;
    float glow = smoothstep(1.0, 0.0, r);
    float core = smoothstep(0.6, 0.0, r);
    vec3 col = mix(uColorEdge, uColorHot, core);
    float a = glow * glow * vAlpha;
    gl_FragColor = vec4(col * a, a);
  }
`;

export default function ShaderFX({ variant = "embers", intensity = 1, className = "" }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false, powerPreference: "low-power" });
    const dpr = Math.min(window.devicePixelRatio || 1, isMobile ? 1.5 : 2);
    // Cap the largest backing-buffer dimension so very tall sections don't
    // allocate huge framebuffers (prevents GPU memory exhaustion / white flash).
    const MAX_BUFFER = isMobile ? 1500 : 2400;
    renderer.setPixelRatio(dpr);
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.display = "block";
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.Camera();

    const uniforms = {
      uTime: { value: 0 },
      uRes: { value: new THREE.Vector2(1, 1) },
      uMouse: { value: new THREE.Vector2(0, 0) },
      uIntensity: { value: intensity },
      uAspect: { value: 1 },
      uPixelRatio: { value: dpr },
      uColor: { value: new THREE.Color("#d91616") },
      uColorHot: { value: new THREE.Color("#ffb24a") },
      uColorEdge: { value: new THREE.Color("#d91616") },
    };

    let mesh;
    if (variant === "embers") {
      const count = isMobile ? 120 : 260;
      const geo = new THREE.BufferGeometry();
      const pos = new Float32Array(count * 3);
      const data = new Float32Array(count * 4);
      for (let i = 0; i < count; i++) {
        pos[i * 3] = Math.random() * 2 - 1;
        pos[i * 3 + 1] = Math.random() * 2 - 1;
        pos[i * 3 + 2] = 0;
        data[i * 4] = 0;
        data[i * 4 + 1] = 0.5 + Math.random() * 1.4; // speed
        data[i * 4 + 2] = 0.5 + Math.random() * 1.5; // sizeSeed
        data[i * 4 + 3] = Math.random(); // phase
      }
      geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      geo.setAttribute("aData", new THREE.BufferAttribute(data, 4));
      const mat = new THREE.ShaderMaterial({
        uniforms,
        vertexShader: EMBER_VERT,
        fragmentShader: EMBER_FRAG,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      mesh = new THREE.Points(geo, mat);
    } else {
      const frag = variant === "ink" ? INK_FRAG : KINTSUGI_FRAG;
      const mat = new THREE.ShaderMaterial({
        uniforms,
        vertexShader: PATTERN_VERT,
        fragmentShader: frag,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });
      mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
    }
    scene.add(mesh);

    // Mouse tracking (relative to this section), smoothed
    const target = new THREE.Vector2(0, 0);
    const onPointer = (e) => {
      const r = mount.getBoundingClientRect();
      const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
      const ny = (1 - (e.clientY - r.top) / r.height) * 2 - 1;
      target.set(nx, ny);
    };
    window.addEventListener("pointermove", onPointer, { passive: true });

    const resize = () => {
      const w = mount.clientWidth || 1;
      const h = mount.clientHeight || 1;
      // clamp effective pixel ratio so max(w,h)*ratio never exceeds MAX_BUFFER
      const effDpr = Math.max(0.75, Math.min(dpr, MAX_BUFFER / Math.max(w, h)));
      renderer.setPixelRatio(effDpr);
      renderer.setSize(w, h, false);
      uniforms.uRes.value.set(w, h);
      uniforms.uAspect.value = w / h;
      uniforms.uPixelRatio.value = effDpr;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(mount);

    // Graceful recovery if the GPU drops this context.
    const canvas = renderer.domElement;
    let lost = false;
    const onCtxLost = (e) => { e.preventDefault(); lost = true; };
    const onCtxRestored = () => {
      lost = false;
      resize();
      renderer.render(scene, camera);
    };
    canvas.addEventListener("webglcontextlost", onCtxLost, false);
    canvas.addEventListener("webglcontextrestored", onCtxRestored, false);

    let visible = true;
    const io = new IntersectionObserver(
      (entries) => { visible = entries[0].isIntersecting; },
      { threshold: 0 }
    );
    io.observe(mount);

    let raf;
    const clock = new THREE.Clock();
    const loop = () => {
      raf = requestAnimationFrame(loop);
      if (!visible || document.hidden || lost) return;
      uniforms.uTime.value += clock.getDelta();
      uniforms.uMouse.value.lerp(target, 0.06);
      renderer.render(scene, camera);
    };
    loop();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onPointer);
      canvas.removeEventListener("webglcontextlost", onCtxLost, false);
      canvas.removeEventListener("webglcontextrestored", onCtxRestored, false);
      ro.disconnect();
      io.disconnect();
      mesh.geometry.dispose();
      mesh.material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line
  }, [variant, intensity]);

  return (
    <div
      ref={mountRef}
      aria-hidden="true"
      data-testid={`shaderfx-${variant}`}
      className={`pointer-events-none ${className}`}
    />
  );
}
